/**
 * Builds the catalogue pipeline's purchased goods import from the wizard, and
 * turns its 400 into a message the spreadsheet importer can show.
 *
 * The catalogue endpoint takes JSON rather than the mixed-import FormData:
 * camelCase rows, dates as YYYY-MM-DD and spend as a plain decimal. The
 * original file isn't sent — the endpoint only references files already in
 * the upload bucket.
 */
import {
  ActivityTypesEnum,
  PurchasedGoodsImportRequest,
  PurchasedGoodsImportRowErrors,
} from '@shared/api/types';
import { formatUKDateToISO } from '@shared/utils/date-formatters';
import type { AxiosError } from 'axios';
import { ActivityType, parseNumberWithCommas } from '../utils';
import { GoodsImportData } from './types';

export interface CatalogueImportValues {
  activityTypes: ActivityType[];
  timePeriodYear?: string;
  timePeriodMonthIndex?: string;
  siteUuid?: string;
  tags?: string[];
}

const MAX_LISTED_ROWS = 5;
const DEFAULT_ERROR = 'The import could not be sent. Please try again.';

/** A spend as the endpoint's plain decimal; an unreadable one is sent as typed so the backend reports its row. */
const toDecimalString = (value: string | undefined): string | null => {
  if (!value) return null;
  const parsed = parseNumberWithCommas(value.replace(/[^\d.,-]/g, ''));
  return Number.isFinite(parsed) ? parsed.toFixed(2) : value;
};

/** A UK date as YYYY-MM-DD; the endpoint refuses full timestamps. */
const toISODate = (value: string | undefined): string | null => {
  if (!value) return null;
  return formatUKDateToISO(value)?.slice(0, 10) ?? null;
};

export const buildPurchasedGoodsImportRequest = (
  rows: GoodsImportData,
  values: CatalogueImportValues
): PurchasedGoodsImportRequest => ({
  activityTypes: values.activityTypes as ActivityTypesEnum[],
  timePeriodYear: values.timePeriodYear || null,
  timePeriodMonthIndex: values.timePeriodMonthIndex
    ? Number(values.timePeriodMonthIndex)
    : null,
  site: values.siteUuid || null,
  tags: values.tags ?? [],
  rows: rows.map((row) => ({
    name: row.name as string,
    inputQuantity: parseNumberWithCommas(row.quantity as string),
    inputUnit: (row.unit as string | undefined) || null,
    externalId: (row.remoteId as string | undefined) || null,
    supplier: (row.supplier as string | undefined) || null,
    date: toISODate(row.date as string | undefined),
    site: (row.site as string | undefined) || null,
    totalSpend: toDecimalString(row.totalSpend as string | undefined),
  })),
});

const isRowErrors = (value: unknown): value is PurchasedGoodsImportRowErrors =>
  typeof value === 'object' && value !== null && 'index' in value;

/** The first rejected rows (numbered as the sheet shows them) or the root errors, one per line. */
export const summarisePurchasedGoodsImportError = (error: unknown): string => {
  const data = (error as AxiosError<Record<string, unknown>> | null)?.response
    ?.data;
  if (!data || typeof data !== 'object') return DEFAULT_ERROR;

  const rowErrors = Array.isArray(data.rows)
    ? data.rows.filter(isRowErrors)
    : [];
  if (rowErrors.length > 0) {
    const listed = rowErrors
      .slice(0, MAX_LISTED_ROWS)
      .map(
        ({ index, errors }) =>
          `Row ${index + 1}: ${errors
            .map(({ field, message }) => `${field} ${message}`)
            .join('; ')}`
      );
    const remaining = rowErrors.length - listed.length;
    if (remaining > 0) listed.push(`…and ${remaining} more rows`);
    return listed.join('\n');
  }

  const rootErrors = Object.entries(data).map(
    ([field, messages]) => `${field}: ${[messages].flat().join(' ')}`
  );
  return rootErrors.length > 0 ? rootErrors.join('\n') : DEFAULT_ERROR;
};
